import { PropsWithChildren, ReactElement } from 'react';

import { Icon as UIIcon } from '@breathelife/ui-components';
import { t } from 'i18next';

import { ComprehensivePricingProductContainer } from './Styles';

import { Box, Tooltip } from '@breathelife/mui';
import { ComprehensivePricingCurrency } from '@breathelife/types';
import Typography, { ItalicTypography } from '../../../Typography';

import { IconNames } from '@zinnia/form-engine-sdk';
import { useTheme } from '../../../../Styles/themed-styled-components';
import { Icon } from '../../../Icons/Icon';
import { costToCostWithCurrency } from './helpers';

export enum ComprehensivePricingWidgetTooltipType {
  ado = 'ado',
  policyFee = 'policyFee',
}

function ComprehensivePricingWidgetTooltip(props: {
  type: ComprehensivePricingWidgetTooltipType;
  message?: string;
}): ReactElement {
  const theme = useTheme();
  let message: string = '';
  switch (props.type) {
    case ComprehensivePricingWidgetTooltipType.ado:
      message = t('assistedApplication.comprehensivePricingWidget.adoTooltipText');
      break;
    case ComprehensivePricingWidgetTooltipType.policyFee:
      message = props.message || '';
      break;
  }

  return (
    <Tooltip title={message}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Icon name='info' width='16px' height='16px' stroke={theme.colors.primary[40]} />
      </Box>
    </Tooltip>
  );
}

type ComprehensivePricingWidgetProductCardProps = {
  displayName: string;
  type: 'product' | 'rider' | 'subTotal' | 'esa';
  cost?: number;
  isJointProduct?: boolean;
  coverageAmount?: number;
  currency?: ComprehensivePricingCurrency;
  isDuplicateCost?: boolean;
  isFirstInList?: boolean;
  tooltipType?: ComprehensivePricingWidgetTooltipType;
  tooltipMessage?: string;
  equivalentSingleAge?: number;
};

export function ComprehensivePricingWidgetProductCard(
  props: PropsWithChildren<ComprehensivePricingWidgetProductCardProps>,
): ReactElement {
  const theme = useTheme();

  const displayedCost =
    props.isDuplicateCost || !props.currency ? '-' : costToCostWithCurrency(props.cost || 0, props.currency);

  return (
    <Box mt={props.isFirstInList ? 0 : props.type === 'rider' ? 1 : 2} ml={props.type === 'rider' ? 2 : 0}>
      <ComprehensivePricingProductContainer>
        <div>
          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
            <Typography variant={props.type === 'subTotal' ? 'h4' : 'body1'} component='span'>
              {props.displayName}
            </Typography>
            {props.isJointProduct && (
              <Box sx={{ marginLeft: '4px', display: 'flex', alignItems: 'center', paddingTop: '2px' }}>
                <UIIcon name={IconNames.group} size='20px' color={{ primary: theme.colors.grey[70] }} />
              </Box>
            )}
            {props.tooltipType && (
              <Box ml={1}>
                <ComprehensivePricingWidgetTooltip type={props.tooltipType} message={props.tooltipMessage} />
              </Box>
            )}
          </Box>
          {props.type !== 'esa' && props.currency && props.coverageAmount && (
            <div>
              <ItalicTypography variant='small1' grey={70} noWrap>
                {costToCostWithCurrency(props.coverageAmount, props.currency, true)}
              </ItalicTypography>
            </div>
          )}
        </div>
        {(props.cost || props.cost === 0) && (
          <Box pl={3}>
            <Typography variant={props.type === 'subTotal' ? 'h4' : 'body1'} noWrap>
              {displayedCost}
            </Typography>
          </Box>
        )}
        {props.equivalentSingleAge && props.type === 'esa' && (
          <Box pl={3}>
            <Typography variant={'body1'} noWrap>
              {props.equivalentSingleAge}
            </Typography>
          </Box>
        )}
      </ComprehensivePricingProductContainer>
      {props.children && <div>{props.children}</div>}
    </Box>
  );
}
